/* eslint-disable no-nested-ternary */
import { LogoutOutlined } from "@ant-design/icons";
import { School } from "@axl-dev/school-sdk";
import { Button, Modal } from "antd";
import { useEffect, useMemo } from "react";

import { useAuth, useDrawer, useSchool } from "@app/shared/lib/hooks";
import { TimeSpan } from "@app/shared/lib/TimeSpan";

import { HubConnectionStatus } from "../HubConnectionStatus";
import { Loc } from "../Loc";

import { AnnouncementMessageQueue } from "./components/AnnouncementMessage/AnnouncementMessageQueue";
import { EmailConfirmationBlocker } from "./components/EmailConfirmationBlocker/EmailConfirmationBlocker";
import { EmailConfirmationMessage } from "./components/EmailConfirmationMessage/EmailConfirmationMessage";
import { StudentPasswordBlocker } from "./components/StudentPasswordBlocker/StudentPasswordBlocker";
import { StudentProfileBlocker } from "./components/StudentProfileBlocker/StudentProfileBlocker";
import { StudentProfileModal } from "./components/StudentProfileModal/StudentProfileModal";
import { useUIAppState } from "./UIAppStateProvider";

const SchoolMigrationBlocker: React.FC<{ school: School }> = ({ school }) => {
  const migrationBlocker = useDrawer();
  const { logout } = useAuth();

  useEffect(() => {
    if (!school.migration) return;
    if (school.migration.isActive || school.migration.isCompleted) {
      migrationBlocker.open();
    } else {
      migrationBlocker.close();
    }
  }, [migrationBlocker, school.migration]);

  return (
    <Modal
      open={migrationBlocker.visible}
      closable={false}
      title={
        <Loc
          word="School.migration.status.active.title"
          default="School has been transferred"
        />
      }
      footer={null}
      width={500}
      zIndex={1000}
      destroyOnClose
      data-testid="block-school-migration-modal"
    >
      <Loc
        word="School.migration.status.active.message"
        default={
          "Your account has been transferred to another domain.\nTo enter it, go to {0}"
        }
        className="pre-wrap"
      >
        <a
          href={school.migration?.settings?.targetUrls?.school}
          target="_blank"
          rel="noreferrer"
        >
          {school.migration?.settings?.targetUrls?.school}
        </a>
      </Loc>
      <footer className="flex justify-between">
        <Button
          type="primary"
          icon={<LogoutOutlined />}
          onClick={logout}
          data-testid="account-menu-logout"
        >
          <Loc word="Logout" />
        </Button>
      </footer>
    </Modal>
  );
};

export const UIAppState: React.FC = () => {
  const school = useSchool();
  const UIAppStateCtxValue = useUIAppState();

  const hasBlocker = useMemo(() => {
    return (
      UIAppStateCtxValue.needChangePassword === true ||
      UIAppStateCtxValue.isProfileModalVisible ||
      UIAppStateCtxValue.isEmailConfirmationModalVisible
    );
  }, [
    UIAppStateCtxValue.needChangePassword,
    UIAppStateCtxValue.isProfileModalVisible,
    UIAppStateCtxValue.isEmailConfirmationModalVisible,
  ]);

  return (
    <>
      {!UIAppStateCtxValue.isProfileModalVisible && (
        <AnnouncementMessageQueue />
      )}
      <HubConnectionStatus delay={TimeSpan.fromMinutes(3)} />
      <SchoolMigrationBlocker school={school} />

      {!UIAppStateCtxValue.isSchoolTransferred && (
        <>
          {UIAppStateCtxValue.needChangePassword === true ? (
            <StudentPasswordBlocker />
          ) : UIAppStateCtxValue.isProfileModalVisible ? (
            <StudentProfileBlocker />
          ) : UIAppStateCtxValue.isEmailConfirmationModalVisible ? (
            <EmailConfirmationBlocker />
          ) : (
            <EmailConfirmationMessage />
          )}
          {!hasBlocker && <StudentProfileModal />}
        </>
      )}
    </>
  );
};
